import React from "react";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ProtectedRoute, defaultRouteForRole } from "@/components/ProtectedRoute";
import { Toaster } from "@/components/ui/sonner";
import AdminLayout from "@/components/AdminLayout";
import OperatorLayout from "@/components/OperatorLayout";
import CustomerLayout from "@/components/CustomerLayout";
import LoginPage from "@/pages/Login";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Security from "@/pages/Security";
import DebtInquiry from "@/pages/DebtInquiry";
import AdminDashboard from "@/pages/admin/Dashboard";
import Live from "@/pages/admin/Live";
import History from "@/pages/admin/History";
import Reports from "@/pages/admin/Reports";
import Tariffs from "@/pages/admin/Tariffs";
import AccessList from "@/pages/admin/AccessList";
import Unpaid from "@/pages/admin/Unpaid";
import CameraTest from "@/pages/admin/CameraTest";
import CameraRawLogs from "@/pages/admin/CameraRawLogs";
import Users from "@/pages/admin/Users";
import Plans from "@/pages/admin/Plans";
import CorporateAccounts from "@/pages/admin/CorporateAccounts";
import Settings from "@/pages/admin/Settings";
import AuditLog from "@/pages/admin/AuditLog";
import Kasa from "@/pages/admin/Kasa";
import BankTransfers from "@/pages/admin/BankTransfers";
import PlateSubscriptions from "@/pages/admin/PlateSubscriptions";
import PlateVerifications from "@/pages/admin/PlateVerifications";
import Coupons from "@/pages/admin/Coupons";
import PaymentsLedger from "@/pages/admin/PaymentsLedger";
import SubscriptionPayments from "@/pages/admin/SubscriptionPayments";
import UnmatchedExits from "@/pages/admin/UnmatchedExits";
import KVKK from "@/pages/admin/KVKK";
import Backup from "@/pages/admin/Backup";
import OperatorHome from "@/pages/operator/Home";
import BusinessLayout from "@/components/BusinessLayout";
import BusinessHome from "@/pages/business/BusinessHome";
import OperatorUnpaid from "@/pages/operator/Unpaid";
import CustomerHome from "@/pages/customer/Home";
import CustomerCoupons from "@/pages/customer/Coupons";
import CustomerHistory from "@/pages/customer/History";
import { registerServiceWorker } from "@/lib/push";
import "@/App.css";

function RootRedirect() {
  const { user } = useAuth();
  if (user === undefined) return null;
  return <Navigate to={user ? defaultRouteForRole(user.role) : "/login"} replace />;
}

// Rol grupları — hiyerarşik ayrım
const admin = ["super_admin", "admin"];                          // sadece yönetici
const adminEmp = ["super_admin", "admin", "employee"];           // yönetici + çalışan
const staff = ["super_admin", "admin", "operator", "employee"];  // tüm personel
const operator = ["super_admin", "operator", "admin", "employee"];
const customer = ["customer"];

function App() {
  React.useEffect(() => { registerServiceWorker(); }, []);
  return (
    <AuthProvider>
      <Toaster position="top-right" richColors />
      <BrowserRouter>
        <Routes>
          <Route path="/" element={<RootRedirect />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path="/reset-password" element={<ResetPassword />} />
          <Route path="/debt" element={<DebtInquiry />} />

          <Route path="/admin/dashboard" element={<ProtectedRoute roles={adminEmp}><AdminLayout><AdminDashboard /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/live" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Live /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/history" element={<Navigate to="/admin/reports" replace />} />
          <Route path="/admin/reports" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Reports /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/tariffs" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Tariffs /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/access" element={<ProtectedRoute roles={adminEmp}><AdminLayout><AccessList /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/unpaid" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Unpaid /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/camera-test" element={<ProtectedRoute roles={admin}><AdminLayout><CameraTest /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/camera-raw" element={<ProtectedRoute roles={admin}><AdminLayout><CameraRawLogs /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute roles={admin}><AdminLayout><Users /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/plans" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Plans /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/corporate" element={<ProtectedRoute roles={adminEmp}><AdminLayout><CorporateAccounts /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/audit" element={<ProtectedRoute roles={admin}><AdminLayout><AuditLog /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute roles={admin}><AdminLayout><Settings /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/kasa" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Kasa /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/bank-transfers" element={<ProtectedRoute roles={admin}><AdminLayout><BankTransfers /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/plate-subs" element={<ProtectedRoute roles={adminEmp}><AdminLayout><PlateSubscriptions /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/plate-verifications" element={<ProtectedRoute roles={admin}><AdminLayout><PlateVerifications /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/subscription-payments" element={<ProtectedRoute roles={adminEmp}><AdminLayout><SubscriptionPayments /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/coupons" element={<ProtectedRoute roles={adminEmp}><AdminLayout><Coupons /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/payments-ledger" element={<ProtectedRoute roles={adminEmp}><AdminLayout><PaymentsLedger /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/unmatched" element={<ProtectedRoute roles={staff}><AdminLayout><UnmatchedExits /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/kvkk" element={<ProtectedRoute roles={admin}><AdminLayout><KVKK /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/backup" element={<ProtectedRoute roles={admin}><AdminLayout><Backup /></AdminLayout></ProtectedRoute>} />
          <Route path="/admin/security" element={<ProtectedRoute roles={admin}><AdminLayout><Security /></AdminLayout></ProtectedRoute>} />

          <Route path="/operator/home" element={<ProtectedRoute roles={operator}><OperatorLayout><OperatorHome /></OperatorLayout></ProtectedRoute>} />
          <Route path="/operator/unpaid" element={<ProtectedRoute roles={operator}><OperatorLayout><OperatorUnpaid /></OperatorLayout></ProtectedRoute>} />
          <Route path="/business" element={<ProtectedRoute roles={["business"]}><BusinessLayout><BusinessHome /></BusinessLayout></ProtectedRoute>} />

          <Route path="/customer/home" element={<ProtectedRoute roles={customer}><CustomerLayout><CustomerHome /></CustomerLayout></ProtectedRoute>} />
          <Route path="/customer/coupons" element={<ProtectedRoute roles={customer}><CustomerLayout><CustomerCoupons /></CustomerLayout></ProtectedRoute>} />
          <Route path="/customer/history" element={<ProtectedRoute roles={customer}><CustomerLayout><CustomerHistory /></CustomerLayout></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
